import chroma from "chroma-js";
import distinctColors from "distinct-colors";
import { DataDivisionList, DataDivisionType, ColorRange, ColorByValueStyling } from "src/common/managers/Types";


type dataType = string | number | undefined | null | boolean;
export class DivisionCalculator {

  static sortNumber(a: number, b: number): number {
    return a - b;
  }

  static valuesIsNumber(values: dataType[]): boolean {
    let uniqueValues = [...new Set(values.map((a) => (a !== undefined && a !== null) ? a + "" : ""))];
    let regex = /^-?\d+(\.\d+)?$/;
    return uniqueValues.every((val) => regex.test(val));
  }

  static isDescreteValue(value: dataType): boolean {
    return !(/^-?\d+$/).test(value + "");
  }

  // ensure that the number is returned nummeric (and not string)
  static toN(nbr: dataType): number {
    switch (typeof nbr) {
      case "number":
        return nbr;
      case "string":
        return Number(nbr);
      case "boolean":
        return nbr ? 1 : 0;
      default:
        return 0;
    }
  }

  static bestDivisionTypeForData(data: dataType[]): DataDivisionType {
    let uniqueValues = [...new Set(data.map((a) => (a !== undefined && a !== null) ? a + "" : ""))];
    if (uniqueValues.length <= 8) {
      return DataDivisionType.Discrete;
    }
    let descreteValues = uniqueValues.reduce((acc, val) => this.isDescreteValue(val) ? acc + 1 : acc, 0);
    if (descreteValues / uniqueValues.length > 0.1) {
      return DataDivisionType.Discrete;
    }
    return DataDivisionType.Continous;
  }

  static calculateDescreteDivisions(data: dataType[], numberOfDivs: number = 4): DataDivisionList {
    let valuesAreNumbers = this.valuesIsNumber(data);
    let sorted = [...new Set(data.map((a) => (a !== undefined && a !== null) ? a + "" : ""))].sort((a, b) => (a).localeCompare(b, "da-dk"));
    if (valuesAreNumbers) {
      sorted = sorted.sort((a, b) => this.toN(a) - this.toN(b));
    }

    return {
      type: DataDivisionType.Discrete,
      valuesAreNumbers: valuesAreNumbers,
      divisionTypeSwapAble: valuesAreNumbers ? true : false,
      list: sorted.slice(0, numberOfDivs).map((val) => {
        const count = data.filter((a) => a == val || (val == "" && a == undefined)).length
        return { from: null, to: null, value: val, count: count }
      }),
      otherValuesCount: sorted.length > numberOfDivs ? sorted.length - numberOfDivs : 0
    }
  }

  static calculateContinousDivisions(data: dataType[], numberOfDivs: number = 4): DataDivisionList {
    let columnData: number[] = data.filter((a) => a !== undefined && a !== null).map((a) => this.toN(a));
    let sortedData = [...new Set(columnData.sort(this.sortNumber))];
    let count = sortedData.length;

    let ArrIndex = Array.from({ length: numberOfDivs }, (_, i) => Math.floor(i * count / (numberOfDivs)));
    return {
      type: DataDivisionType.Continous,
      divisionTypeSwapAble: true,
      list: ArrIndex.map((fromIdx, idx) => {
        let toIdx = ArrIndex[idx + 1] ?? null;
        let fromVal = idx === 0 ? null : sortedData[fromIdx];
        let toVal = idx === numberOfDivs - 1 ? null : sortedData[toIdx];
        let count = columnData.filter((a) => (fromVal === null || a > fromVal) && (toVal === null || a <= toVal)).length;
        return { from: fromVal, to: toVal, value: null, count: count }
      }),
      otherValuesCount: 0
    }
  }

  static recalcDivisions(columnDataInput, divisionList: DataDivisionList): DataDivisionList {
    if (divisionList.type == DataDivisionType.Continous) {
      return this.calculateContinousDivisions(columnDataInput, divisionList.list.length);
    } else {
      return this.calculateDescreteDivisions(columnDataInput, divisionList.list.length);
    }
  }

  /**
  * Finds division for number. Returns -1 if not found.
  * @param divisions 
  * @param value 
  * @returns index or -1 if not found
  */
  static getDivisionId(divisions: DataDivisionList, value?: dataType): number {
    if (divisions.list.length === 0) {
      return -1;
    }
    if (divisions.type === DataDivisionType.Discrete) {
      return divisions.list.findIndex((val) => { return val.value === value + "" });
    }
    if (value === undefined || value === null) {
      return -1;
    }
    let numValue = this.toN(value);
    return divisions.list.findIndex((val) => {
      return (numValue <= val.to!) || (val.to === null && numValue > val.from!);
    });
  }

  static getColorRangeForDivision(divisions: DataDivisionList): ColorRange | null {
    const ignoredColors = ["#000", "#000000", "#ffffff", "#fff", "#FFF", "#FFFFFF"];
    return {
      name: "From divisions",
      order: 1,
      colors: divisions.list.map((a) => a.color).filter((a) => a && !ignoredColors.includes(a)) as string[],
      isCustom: true,
    }
  }

  static swapDivisionToDiscrete(dataValues: dataType[], divisions: DataDivisionList): DataDivisionList {
    let nextDicision = this.calculateDescreteDivisions(dataValues || [], divisions.list.length);
    return {
      ...nextDicision,
      valuesAreNumbers: true,
      divisionTypeSwapAble: true,
      list: nextDicision.list.map((a, idx) => {
        return { ...a, color: divisions.list[idx]?.color || a.color }
      })
    }
  }

  static swapDivisionToContinous(dataValues: dataType[], divisions: DataDivisionList): DataDivisionList {
    let nextDicision = this.calculateContinousDivisions(dataValues || [], divisions.list.length);
    return {
      ...nextDicision,
      list: nextDicision.list.map((a, idx) => {
        return { ...a, color: divisions.list[idx]?.color || a.color }
      })
    }
  }
}

export class ColorByValueCalculator {

  static recalcColorByValue(columnData: dataType[], colorByValue: ColorByValueStyling, maxDivisionsForColorRange: number = 8): ColorByValueStyling {
    if (colorByValue.divisions?.list?.length === undefined) {
      return colorByValue;
    }
    let colorsFromDiv: string[] = DivisionCalculator.getColorRangeForDivision(colorByValue.divisions)?.colors || [];
    let colors = chroma.scale([...colorsFromDiv]).mode("lab").colors(colorByValue.divisions?.list.length);
    if (colorByValue.divisions?.list.length > maxDivisionsForColorRange) {
      colors = distinctColors({ count: colorByValue.divisions?.list.length }).map((a) => a.hex());
    }
    let recalcDevisions = DivisionCalculator.recalcDivisions(columnData, colorByValue.divisions);
    return {
      ...colorByValue,
      divisions: { ...recalcDevisions, list: recalcDevisions?.list.map((a, idx) => ({ ...a, color: colors[idx] || "#000" })) }
    }
  }

}